import { defineStore } from "pinia";
import ContactMessage from "@shared-models/box/contact.message.model";
import Session from "@shared-models/planning/session.model";
import EventModel from "@shared-models/event/event.model";
import Subscription from "@shared-models/shop/subscription.model";
import User from "@shared-models/profile/user.model";
import WorkoutType from "@shared-models/planning/workout.type.model";
import MovementCategory from "@shared-models/movement/movement.category.model";
import { getRootCategories } from "@shared-services/movement/movement.public.service";
import CouponApplyToDTO from "@shared-dto/shop/coupon.apply.to.dto";
import { computed, ref, watch } from "vue";
import PlanningFilterInterface from "@shared-interfaces/planning/planning.filter.interface";
import EventFiltersInterface from "@shared-interfaces/event/event.filters.interface";
import EventRegisteringInterface from "@shared-interfaces/event/event.registering.interface";
import EventMarkerInterface from "@shared-interfaces/event/event.marker.interface";
import EventType from "@shared-models/event/event.type.model";
import EventTag from "@shared-models/event/event.tag.model";
import PlanningDayDTO from "@shared-dto/planning/planning.day.dto";
import ActivityLogFiltersInterface from "@shared-interfaces/profile/activity.log.filters.interface";
import PaymentOverviewDTO from "@shared-dto/shop/payment.overview.dto";
import UserSubscription from "@shared-models/shop/user.subscription.model";
import UserSession from "@shared-models/planning/user.session.model";
import UserStatsDTO from "@shared-dto/planning/user.stats.dto";
import PlanningMarkerDayDTO from "@shared-dto/planning/planning.marker.day.dto";
import CoordinatesDTO from "@shared-dto/coordinates.dto";
import { getPendingLostItemsCount, getUnreadLostItemsUserCount } from "@shared-services/box/lost.item.service";
import { getUnreadSurveysUserCount } from "@shared-services/box/survey.service";
import { getUnreadNewsCount } from "@shared-services/box/news.service";
import { getUnreadBroadcastsCount } from "@shared-services/communication/broadcast.service";
import { getNextUserSessions, getStats } from "@shared-services/planning/user.session.service";
import { updateSessionInPlanning, updateUserSessionInPlanning } from "@shared-utils/planning/planning.slides.utils";
import { refreshUser as stateStoreRefreshUser } from "@shared-services/profile/profile.service";
import useLoginState from "@shared-composables/useLoginState";
import useAuthStore from "@shared-stores/auth.store";

const useStateStore = defineStore(
	"state",
	() =>
	{
		// Navigation
		const previousRoute = ref(null);

		// User
		const user = ref<User | null>(null);
		const userInfos = ref<Partial<User> | null>(null);
		const activeUserSubscription = computed(
			{
				get: () => user.value?.activeUserSubscription ?? null,
				set: (val: UserSubscription | undefined) =>
				{
					if (user.value)
					{
						user.value.activeUserSubscription = val;
					}
				}
			});

		const planningMarkersDays = computed({
			get: () => user.value?.planningMarkersDays ?? [],
			set: (val: PlanningMarkerDayDTO[]) =>
			{
				if (user.value)
				{
					user.value.planningMarkersDays = val;
				}
			}
		});

		// Portées par `user` comme `planningMarkersDays` : remises à zéro avec lui, donc un compte
		// n'hérite jamais des inscriptions du précédent.
		const eventsMarkersMonths = computed({
			get: () => user.value?.eventsMarkersMonths ?? [],
			set: (val: EventMarkerInterface[]) =>
			{
				if (user.value)
				{
					user.value.eventsMarkersMonths = val;
				}
			}
		});

		const pendingSessionsCount = computed(
			{
				get: () => user.value?.pendingSessionsCount ?? 0,
				set: (val: number) =>
				{
					if (user.value)
					{
						user.value.pendingSessionsCount = val;
					}
				}
			});
		const nextUserSessions = computed({
			get: () => user.value?.nextUserSessions ?? [],
			set: (val: UserSession[]) =>
			{
				if (user.value)
				{
					user.value.nextUserSessions = val;
				}
			}
		});
		const userStats = computed({
			get: () => user.value?.userStats ?? null,
			set: (val: UserStatsDTO | null) =>
			{
				if (user.value)
				{
					user.value.userStats = val ?? undefined;
				}
			}
		});
		const unreadNewsCount = computed({
			get: () => user.value?.unreadNewsCount ?? 0,
			set: (val: number) =>
			{
				if (user.value)
				{
					user.value.unreadNewsCount = val;
				}
			}
		});
		const unreadBroadcastsCount = computed({
			get: () => user.value?.unreadBroadcastsCount ?? 0,
			set: (val: number) =>
			{
				if (user.value)
				{
					user.value.unreadBroadcastsCount = val;
				}
			}
		});
		const unreadSurveysCount = computed({
			get: () => user.value?.unreadSurveysCount ?? 0,
			set: (val: number) =>
			{
				if (user.value)
				{
					user.value.unreadSurveysCount = val;
				}
			}
		});
		const unreadLostItemsCount = computed({
			get: () => user.value?.unreadLostItemsCount ?? 0,
			set: (val: number) =>
			{
				if (user.value)
				{
					user.value.unreadLostItemsCount = val;
				}
			}
		});
		const unreadPurchasesCount = computed({
			get: () => user.value?.unreadPurchasesCount ?? 0,
			set: (val: number) =>
			{
				if (user.value)
				{
					user.value.unreadPurchasesCount = val;
				}
			}
		});
		const awaitingPickupPurchasesCount = computed<number>(() => user.value?.awaitingPickupPurchasesCount ?? 0);
		const unreadEventsCount = computed({
			get: () => user.value?.unreadEventsCount ?? 0,
			set: (val: number) =>
			{
				if (user.value)
				{
					user.value.unreadEventsCount = val;
				}
			}
		});
		const pendingLostItemsCount = computed({
			get: () => user.value?.pendingLostItemsCount ?? 0,
			set: (val: number) =>
			{
				if (user.value)
				{
					user.value.pendingLostItemsCount = val;
				}
			}
		});
		const upcomingEventsCount = computed({
			get: () => user.value?.upcomingEventsCount ?? 0,
			set: (val: number) =>
			{
				if (user.value)
				{
					user.value.upcomingEventsCount = val;
				}
			}
		});
		const scoresToNoteCount = computed({
			get: () => user.value?.scoresToNoteCount ?? 0,
			set: (val: number) =>
			{
				if (user.value)
				{
					user.value.scoresToNoteCount = val;
				}
			}
		});
		const badgeCount =
			computed(() =>
				unreadNewsCount.value +
				unreadBroadcastsCount.value +
				unreadSurveysCount.value +
				unreadLostItemsCount.value +
				unreadPurchasesCount.value
			);

		// Vérification du compte si l'utilisateur se connecte
		const userNeedsToVerifyAccount = ref<boolean>(false);

		/**
		 * Le fil est-il à l'écran ?
		 *
		 * Deux écoutes répondent à une publication : le fil, qui insère le message et le
		 * marque lu, et l'initializer, qui relit le compteur pour la pastille. La seconde part
		 * en premier et peut rendre « 1 » après que la première a posé « 0 » — la pastille
		 * reste alors allumée sur un message que l'adhérent a sous les yeux. Quand le fil est
		 * ouvert, c'est lui qui fait foi.
		 */
		const isBroadcastFeedOpen = ref<boolean>(false);

		// Activity Logs
		const activityLogsFilters = ref<ActivityLogFiltersInterface>({ categoryKeys: [], sources: [] });

		// Account & Drop-In
		const resetPasswordEmail = ref<string | null>(null);
		const registeringUser = ref<User>({ hasTrySession: true });
		const registeredUser = ref<User | null>(null);
		const registrationToken = ref<string | null>(null);

		// Lors d'un achat
		const chosenSubscription = ref<Subscription | null>(null);
		const couponApplyTo = ref<CouponApplyToDTO | null>(null);

		/** Tunnel d'abonnement : l'étape 4 lit `isPayNow`, persisté pour survivre au retour 3-D Secure. */
		const paymentOverview = ref<PaymentOverviewDTO | null>(null);

		// WorkoutType
		const workoutTypes = ref<WorkoutType[] | null>(null);

		// Racines de MovementCategory (4 familles matériel, avec couleurs) — carte « Diversité des mouvements ».
		const movementCategoryRoots = ref<MovementCategory[]>([]);

		async function loadMovementCategoryRoots(): Promise<void>
		{
			if (movementCategoryRoots.value.length > 0)
			{
				return;
			}
			movementCategoryRoots.value = await getRootCategories();
		}

		// Planning
		const planningByDayIso = ref<PlanningDayDTO[]>([]);
		// Jours actuellement affichés à l'écran (mis à jour par planning-index.vue) — utilisé pour
		// re-fetcher uniquement ce qui est visible quand un changement nécessite un refresh global.
		const planningSelectedDaysIso = ref<string[]>([]);
		const planningFilters = ref<PlanningFilterInterface>({
			workoutTypes: []
		});

		// Événements — pendants de `planningByDayIso` & co. Clé = mois ISO (`YYYY-MM`).
		const eventTypes = ref<EventType[]>([]);
		const eventTags = ref<EventTag[]>([]);
		const eventsByMonthIso = ref<Map<string, EventModel[]>>(new Map());
		const eventsSelectedMonthsIso = ref<string[]>([]);
		const eventsFilters = ref<EventFiltersInterface>({
			access: [],
			types: [],
			tags: [],
		});
		const registeringEvent = ref<EventRegisteringInterface>({ alias: null, formatId: null });

		// Contact
		const newContactMessage = ref<ContactMessage | null>(null);

		// Coordonnées
		const coordinates = ref<CoordinatesDTO | null>(null);

		// Cache stale : invalider planningByDayIso quand l'abo actif change (transition pas d'abo ↔
		// abo, changement d'abo). Sinon le store garde les status FUTURE_USER_NEEDS_SUBSCRIPTION
		// précédents et l'UI affiche "Actif dès le X" alors que l'API renverrait "S'inscrire".
		watch(
			() => activeUserSubscription.value?.id,
			(newId, oldId) =>
			{
				// On n'invalide QUE sur un vrai changement d'abo à l'exécution. oldId === undefined =
				// première résolution (chargement initial / hydratation, ex. /me qui arrive après le
				// fetch du planning) : les jours viennent d'être chargés sous le bon contexte d'abo —
				// les effacer provoquerait un flash "sessions puis skeleton" à chaque refresh.
				if (oldId !== undefined && newId !== oldId)
				{
					planningByDayIso.value = [];
				}
			}
		);

		function reset()
		{
			// User
			user.value = null;
			userInfos.value = null;

			// Vérification du compte si l'utilisateur se connecte
			userNeedsToVerifyAccount.value = false;

			// Activities Log
			activityLogsFilters.value = { categoryKeys: [], sources: [] };

			// Account & Drop-In
			resetPasswordEmail.value = null;
			registeringUser.value = { hasTrySession: true };
			registeredUser.value = null;
			registrationToken.value = null;

			// Lors d'un achat
			chosenSubscription.value = null;
			couponApplyTo.value = null;

			// Événements
			eventTypes.value = [];
			eventTags.value = [];
			eventsByMonthIso.value = new Map();
			eventsSelectedMonthsIso.value = [];
			eventsFilters.value = {
				access: [],
				types: [],
				tags: [],
			};
			registeringEvent.value = { alias: null, formatId: null };

			// Planning
			planningByDayIso.value = [];
			planningSelectedDaysIso.value = [];
			planningFilters.value = {
				workoutTypes: []
			};

			// WorkoutType
			workoutTypes.value = null;

			// Contact
			newContactMessage.value = null;
		}

		function resetFilters()
		{
			planningFilters.value.workoutTypes = [];
		}

		/**
		 * Actions : refresh ciblés des parties du state depuis l'API.
		 * Appelés principalement depuis les handlers PubSub (Mercure) et les flows user.
		 */
		async function refreshPendingLostItemsCount()
		{
			if (user.value)
			{
				pendingLostItemsCount.value = await getPendingLostItemsCount();
			}
		}

		async function refreshUnreadLostItemsCount()
		{
			if (user.value)
			{
				unreadLostItemsCount.value = await getUnreadLostItemsUserCount();
			}
		}

		async function refreshUnreadSurveysCount()
		{
			if (user.value)
			{
				unreadSurveysCount.value = await getUnreadSurveysUserCount();
			}
		}

		async function refreshUnreadBroadcastsCount()
		{
			if (user.value)
			{
				unreadBroadcastsCount.value = await getUnreadBroadcastsCount();
			}
		}

		async function refreshUnreadNewsCount()
		{
			if (user.value)
			{
				unreadNewsCount.value = await getUnreadNewsCount();
			}
		}

		async function refreshStats()
		{
			if (user.value)
			{
				userStats.value = await getStats();
			}
		}

		async function refreshNextUserSessions()
		{
			if (user.value)
			{
				nextUserSessions.value = await getNextUserSessions();
			}
		}

		// Propage la mise à jour d'une UserSession à la liste planning (planningByDayIso) → /planning à
		// jour au retour sans re-fetch. Si la session complète est fournie (3e arg), on patche aussi la
		// session (status calculé, stats) pour que le badge sur /planning soit cohérent après une
		// cascade auto reçue alors qu'on était sur /planning/:id. La fiche, elle, se relit à chaque visite.
		function syncSessionUserData(sessionId: string, updatedUserSession: UserSession, updatedSession?: Session)
		{
			updateUserSessionInPlanning(planningByDayIso.value, sessionId, updatedUserSession);

			if (updatedSession)
			{
				updateSessionInPlanning(planningByDayIso.value, sessionId, updatedSession);
			}
		}

		async function refreshUser(endpoint: string = "/me")
		{
			const { isLoggedIn } = useLoginState();

			if (isLoggedIn.value)
			{
				user.value = await stateStoreRefreshUser(endpoint);
			}
		}

		async function login(endpoint: string = "/me")
		{
			await refreshUser(endpoint);
		}

		function logout()
		{
			useAuthStore().logout();
			reset();
		}

		return {
			previousRoute,

			// User
			user,
			userInfos,
			activeUserSubscription,
			planningMarkersDays,
			pendingSessionsCount,
			nextUserSessions,
			userStats,
			unreadNewsCount,
			unreadBroadcastsCount,
			unreadSurveysCount,
			unreadLostItemsCount,
			unreadPurchasesCount,
			awaitingPickupPurchasesCount,
			unreadEventsCount,
			badgeCount,

			// Vérification du compte si l'utilisateur se connecte
			userNeedsToVerifyAccount,

			// Fil « Au quotidien »
			isBroadcastFeedOpen,

			// Objets trouvés
			pendingLostItemsCount,

			// Événements
			upcomingEventsCount,

			// Scores à noter
			scoresToNoteCount,

			// Activities Log
			activityLogsFilters,

			// Account & Drop-In
			resetPasswordEmail,
			registeringUser,
			registeredUser,
			registrationToken,

			// Lors d'un achat
			chosenSubscription,
			couponApplyTo,
			paymentOverview,

			// Événements
			eventTypes,
			eventTags,
			eventsByMonthIso,
			eventsSelectedMonthsIso,
			eventsFilters,
			registeringEvent,
			eventsMarkersMonths,

			// Planning
			planningByDayIso,
			planningSelectedDaysIso,
			planningFilters,

			// WorkoutType
			workoutTypes,
			movementCategoryRoots,
			loadMovementCategoryRoots,

			// Contact Message
			newContactMessage,

			// Coordonnées
			coordinates,

			reset,
			resetFilters,

			// Actions (refresh ciblés depuis l'API)
			refreshUser,
			login,
			logout,
			refreshPendingLostItemsCount,
			refreshUnreadLostItemsCount,
			refreshUnreadSurveysCount,
			refreshUnreadNewsCount,
			refreshUnreadBroadcastsCount,
			refreshStats,
			refreshNextUserSessions,
			syncSessionUserData,
		};
	},
	{
		// @ts-ignore
		persist: [
			{
				pick: [
					// User
					"user",
					"userInfos",

					// Vérification du compte si l'utilisateur se connecte
					"userNeedsToVerifyAccount",
				],
				storage: localStorage
			},
			{
				pick: [
					"activityLogsFilters",

					// Account & Drop-In
					"resetPasswordEmail",
					"registeringUser",
					"registeredUser",
					"registrationToken",

					// Lors d'un achat
					"chosenSubscription",
					"couponApplyTo",
					"paymentOverview",

					// Planning
					"planningFilters",

					// Événements
					"eventsFilters",
					"registeringEvent",

					// Contact message
					"newContact"
				],
				storage: sessionStorage
			}
		]
	}
);

export default useStateStore;
